<template>
  <div>
    <Html :lang="localeHead.htmlAttrs.lang" :dir="localeHead.htmlAttrs?.dir">
      <Body>
        <!-- Reference Container for global positioning -->
        <div class="reference-container my-0 py-0 container container--page-content" aria-hidden="true">
          <div ref="referenceContainer" class="reference-container__item w-full h-px opacity-0 pointer-events-none" />
        </div>

        <GlobalToast />

        <!-- Header -->
        <SharedPicture
          :media="siteOptions?.data?.siteLogo"
          class="!h-[50px] !w-[246px] object-contain object-center !hidden print:!block"
          :quality="100"
          loading="eager"
          fetchpriority="high"
          height="50"
          width="246"
          :alt="siteOptions?.data?.siteLogo?.alternativeText || ''"
          :show-placeholder="false"
        />
        <Header v-if="siteOptions?.data" :site-options="siteOptions.data" class="fixed w-full top-0 z-20 print:!hidden" />
        <SharedRouteLoadingSpinner class="z-30" aria-hidden="true" />

        <!-- Main Content -->
        <main class="flex flex-1 justify-center relative z-10 pt-[var(--header-height)]">
          <slot />
        </main>
        <GoToTop class="goto-top-button" />
        <SharedBuyVoucherModal />
        <!-- Santander financing calculator, behind the financingCalculator feature switch -->
        <SharedFinancingModal v-if="financingCalculator" />
        <SharedBookServiceModal />
        <SharedAIChat />

        <div id="floating-actions-container" class="print:!hidden"></div>
        <!-- Footer -->
        <Footer v-if="siteOptions?.data" class="bottom-0 z-10" />
      </Body>
    </Html>
  </div>
</template>

<script setup lang="ts">
  import type { SiteOption, StrapiResponse } from '~/types/strapi';

  const financingCalculator = useFeature('financingCalculator');

  // Fetch site options with unstorage caching
  const { locale } = useI18n();
  const siteOptions = ref<StrapiResponse<SiteOption> | null>(null);
  const { data: siteOptionsData } = await useSiteOptions(locale.value);
  siteOptions.value = siteOptionsData.value || null;

  watch(locale, async () => {
    const { data: siteOptionsData } = await useSiteOptions(locale.value);
    siteOptions.value = siteOptionsData.value || null;
  });

  // Provide site options to child components
  provide('siteOptions', siteOptions);

  // Setup reference container for global positioning
  const { referenceContainer, left, x, width, height } = useReferenceContainer();

  // Provide reference container data to child components
  // usefull for computed style positioning
  provide('referenceContainer', {
    referenceContainer,
    left,
    x,
    width,
    height,
  });

  const route = useRoute();
  const localeHead = useLocaleHead({ lang: true });

  // Default SEO from site options
  const defaultSeo = computed(() => siteOptions.value?.data?.seo);

  // Page-specific SEO (from route meta)
  const pageSeo = computed(() => ({
    metaTitle: route.meta.title as string,
    metaDescription: route.meta.metaDescription as string,
    structuredData: route.meta.structuredData,
    ogTitle: route.meta.ogTitle as string,
    ogDescription: route.meta.ogDescription as string,
    ogImage: route.meta.ogImage as string,
    ogUrl: route.meta.ogUrl as string,
    ogType: route.meta.ogType as string,
    ogLocale: route.meta.ogLocale as string,
  }));

  // Combine page SEO with default SEO
  const combinedSeo = computed(() => {
    if (!defaultSeo.value) return pageSeo.value;

    return {
      ...defaultSeo.value,
      ...pageSeo.value,
      // Only override if page has specific values
      metaTitle: pageSeo.value?.metaTitle || defaultSeo.value.metaTitle,
      metaDescription: pageSeo.value?.metaDescription || defaultSeo.value.metaDescription,
      // Use page structured data if available (check for both undefined and null), otherwise use default
      structuredData: pageSeo.value?.structuredData !== undefined && pageSeo.value?.structuredData !== null ? pageSeo.value.structuredData : defaultSeo.value.structuredData,
    };
  });

  // Register SEO head tags in setup (avoids Nuxt context errors)
  useStrapiSeo(combinedSeo);

  // Site-wide LocalBusiness/AutoDealer JSON-LD (addresses + opening hours)
  useBusinessSchemaOrg();

  // Combine locale head with our custom head elements
  const combinedHead = computed(() => {
    const baseHead = {
      htmlAttrs: localeHead.value.htmlAttrs,
      link: [
        ...localeHead.value.link, // This includes hreflang alternates and canonical
      ],
    };

    return baseHead;
  });

  useHead(combinedHead);

</script>

<style lang="scss" scoped>
  .goto-top-button {
    @apply fixed bottom-10 right-2 lg:right-16 z-30 print:!hidden;
  }
</style>

<style lang="scss">
  main:has(.page.page--vehicle-detail) ~ .goto-top-button {
    @media (max-width: 768px) {
      @apply bottom-[150px];
    }
  }

  #__nuxt:has(.page.page--vehicle-detail) .footer {
    @media (max-width: 767px) {
      @apply mb-[140px];
    }
    @screen md {
      @apply mb-[80px];
    }
  }

  body:has(.page.page--vehicle-detail) #CookiebotWidget {
    transition: all 0.15s ease-in-out;
  }

  body:has(.page.page--vehicle-detail):has(.floating-actions) #CookiebotWidget:not(.CookiebotWidget-open) {
    bottom: 90px !important;
    left: 20px !important;

    @media (min-width: 768px) {
      bottom: 115px !important;
    }
  }

  header:has(.mobile-menu__overlay) ~ #floating-actions-container {
    @media (max-width: 768px) {
      @apply hidden;
    }
  }

  header:has(.mobile-menu__overlay) ~ .goto-top-button {
    @media (max-width: 1023px) {
      @apply hidden;
    }
  }
</style>
